import React, { Component, ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import DevApp from './pages/DevApp';
import DevLogin from './pages/DevLogin';
import DevChangePassword from './pages/DevChangePassword';
import DevService from './pages/DevService';
import DevApiCredit from './pages/DevApiCredit';
import DevApiCreditUsage from './pages/DevApiCreditUsage';
import DevForecastPush from './pages/DevForecastPush';
import DevLightningPush from './pages/DevLightningPush';
import PublicDocs from './pages/PublicDocs';
import PublicDocService from './pages/PublicDocService';
import Health from './pages/Health';
import DevKeys from './pages/DevKeys';

const router = createBrowserRouter([
  { path: '/login', element: <DevLogin /> },
  // Public docs (no auth required)
  { path: '/', element: <PublicDocs /> },
  { path: '/docs', element: <PublicDocs /> },
  { path: '/docs/:svc', element: <PublicDocService /> },
  {
    path: '/portal',
    element: <RequireDevAuth><DevApp /></RequireDevAuth>,
    children: [
      { index: true, element: <Health /> },
      { path: 'health', element: <Health /> },
      { path: 'usage', element: <DevApiCreditUsage /> },
      { path: 'docs', element: <Navigate to="services" replace /> },
      { path: 'services/forecast-push', element: <DevForecastPush /> },
      { path: 'services/forecast-push/access', element: <DevForecastPush /> },
      { path: 'services/forecast-push/webhook', element: <DevForecastPush /> },
      { path: 'services/forecast-push/webhook-test', element: <DevForecastPush /> },
      { path: 'services/forecast-push/rules', element: <DevForecastPush /> },
      { path: 'services/forecast-push/locations', element: <DevForecastPush /> },
      { path: 'services/lightning-push', element: <DevLightningPush /> },
      { path: 'services/lightning-push/access', element: <DevLightningPush /> },
      { path: 'services/lightning-push/webhook', element: <DevLightningPush /> },
      { path: 'services/lightning-push/regions', element: <DevLightningPush /> },
      { path: 'services/:svc/access', element: <DevService /> },
      { path: 'services/:svc/api', element: <DevService /> },
      { path: 'services/:svc', element: <DevService /> },
      { path: 'services', element: <div><h2>Services</h2><div className="muted">Select a service from the sidebar to view details and docs.</div></div> },
      { path: 'keys', element: <DevKeys /> },
      { path: 'credit', element: <DevApiCredit /> },
      { path: 'change-password', element: <DevChangePassword /> },
      { path: '*', element: <Navigate to="services" replace /> },
    ],
  },
]);

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    let p = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (p.length % 4 !== 0) p += '=';
    const payload = JSON.parse(atob(p));
    const exp = typeof payload.exp === 'number' ? payload.exp : null;
    return exp;
  } catch { return null; }
}

function RequireDevAuth({ children }: { children: JSX.Element }) {
  const token = localStorage.getItem('afw.dev.token') || '';
  if (!token) return <DevLogin />;
  const exp = decodeJwtExp(token);
  const nowSec = Math.floor(Date.now() / 1000);
  if (exp && exp <= nowSec) {
    try { localStorage.removeItem('afw.dev.token'); } catch {}
    return <DevLogin />;
  }
  return children;
}

class DevErrorBoundary extends Component<{ children: ReactNode }, { err?: any }> {
  constructor(props: any) { super(props); this.state = {}; }
  static getDerivedStateFromError(err: any) { return { err }; }
  componentDidCatch(error: any, info: any) {
    try { console.error('[DevPortal] ErrorBoundary', error, info); } catch {}
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 24, fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' }}>
          <h2>Developer Portal Error</h2>
          <div style={{ color: 'crimson', whiteSpace: 'pre-wrap' }}>{String(this.state.err?.message || this.state.err)}</div>
          <div className="muted" style={{ marginTop: 8 }}>Open the browser console for stack details.</div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Global listeners to surface errors in console for diagnosis
try {
  (window as any).__afwErrs = [];
  window.addEventListener('error', (e) => {
    try { console.error('[DevPortal] window.error', e.message, e.filename, e.lineno, e.colno, e.error); } catch {}
    (window as any).__afwErrs.push({ type: 'error', msg: e.message, stack: e.error && e.error.stack });
  });
  window.addEventListener('unhandledrejection', (e: any) => {
    try { console.error('[DevPortal] unhandledrejection', e.reason); } catch {}
    (window as any).__afwErrs.push({ type: 'unhandledrejection', reason: e.reason });
  });
} catch {}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <DevErrorBoundary>
      <RouterProvider router={router} />
    </DevErrorBoundary>
  </React.StrictMode>
);
